import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import authService from '../../api/authService';
import api from '../../api/api';
import { registerGlobalLogout } from '../../store/authSession';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await api.get('/api/users/me');
      setUser(response.data);
    } catch {
      // Токен невалиден — сбрасываем сессию
      localStorage.removeItem('token');
      setIsAuthenticated(false);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsAuthenticated(true);
      fetchCurrentUser().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [fetchCurrentUser]);

  const loginWithGoogle = async (googleToken) => {
    try {
      const response = await authService.authenticateWithGoogle(googleToken);
      const { token } = response.data;
      
      // Store token in localStorage
      localStorage.setItem('token', token);
      
      // Set authentication state
      setIsAuthenticated(true);
      await fetchCurrentUser();
      
      return { success: true };
    } catch (error) {
      console.error('Google login failed:', error);
      return { success: false, error: error.message };
    }
  };

  const register = async (name, email, password) => {
    try {
      const response = await authService.register(name, email, password);
      const { token } = response.data;

      // Store token in localStorage
      localStorage.setItem('token', token);

      setIsAuthenticated(true);
      await fetchCurrentUser();

      return { success: true };
    } catch (error) {
      console.error('Registration failed:', error);
      return { success: false, error: error.message };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      const { token } = response.data;

      // Store token in localStorage
      localStorage.setItem('token', token);

      setIsAuthenticated(true);
      await fetchCurrentUser();

      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUser(null);
  }, []);
  // Устанавливаем глобальный logout при монтировании
  useEffect(() => {
    registerGlobalLogout(logout);
    return () => registerGlobalLogout(null);
  }, [logout]);

  const value = {
    isAuthenticated,
    user,
    setUser,
    refreshCurrentUser: fetchCurrentUser,
    loginWithGoogle,
    register,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
